import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { getMe, logout } from '@/lib/api/admin';
import { ApiError } from '@/lib/api/client';
import { hasSession, onAuthExpired } from '@/lib/api/tokens';
import { Button } from '@/components/ui/button';
import { QueryError, Skeleton } from '@/components/ui/feedback';
import { toast } from '@/components/ui/toast';
import { Shell } from '@/components/shell/shell';

/**
 * The gate. No refresh token → the login page, remembering where to return.
 * With one, `/auth/me` decides: `isSuperAdmin`, not `role` — a membership
 * wins in `role`, so a platform admin who also owns an organization reads
 * `role: 'owner'`, and gating on that would deny exactly the accounts the
 * backend's `SuperAdminGuard` admits. Login already refuses non-admins, so
 * the denied screen is for a flag that changed under a live session.
 */
export function RequireAuth() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signedIn = hasSession();

  const me = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 5 * 60_000, enabled: signedIn });

  useEffect(() => onAuthExpired(() => {
    qc.clear();
    toast('info', 'Сессия истекла', 'Войдите снова.');
    navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
  }), [qc, navigate, location.pathname, location.search]);

  const signOut = useMutation({ mutationFn: logout, onSettled: () => { qc.clear(); navigate('/login', { replace: true }); } });

  if (!signedIn) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  if (me.isPending) {
    return (
      <div className="flex min-h-screen">
        <div className="hidden w-60 bg-sidebar md:block" />
        <div className="flex-1 p-8">
          <Skeleton className="mb-6 h-7 w-48" />
          <div className="flex gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 flex-1" />)}</div>
        </div>
      </div>
    );
  }

  if (me.isError) {
    if (ApiError.is(me.error) && me.error.statusCode === 401) return <Navigate to="/login" replace />;
    return <div className="mx-auto max-w-md p-6 pt-24"><QueryError error={me.error} onRetry={() => me.refetch()} /></div>;
  }

  if (!me.data.isSuperAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <ShieldOff size={44} className="text-faint" />
        <h1 className="text-lg font-bold text-ink">Доступ запрещён</h1>
        <p className="max-w-sm text-sm text-muted">Этот раздел доступен только администраторам платформы. Вы вошли как {me.data.email}.</p>
        <Button variant="outline" loading={signOut.isPending} onClick={() => signOut.mutate()}>Выйти</Button>
      </div>
    );
  }

  return <Shell me={me.data}><Outlet /></Shell>;
}
